import { Duration } from '@bufbuild/protobuf/wkt'
import { AddOutlined } from '@mui/icons-material'
import {
    DataGrid,
    GridColDef,
    GridPaginationModel,
    GridSortModel,
} from '@mui/x-data-grid'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { lightFormat } from 'date-fns'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod/v4-mini'
import { ConfirmationDialog } from '@/components/ConfirmationDialog'
import { FabLink } from '@/components/FabLink'
import { AdminOverviewPagePendingComponent } from '@/features/admin/components/AdminOverviewPagePendingComponent'
import { CustomAdminDataGridToolbar } from '@/features/admin/components/CustomAdminDataGridToolbar'
import { assertUserIsAdminOrRedirect } from '@/features/admin/helper/assertUserIsAdminOrRedirect'
import { workingTimeModelFieldNameAndSortByEnumMap } from '@/features/admin/working-time-models/overview/helper/workingTimeModelFieldNameAndSortByEnumMap'
import { workingTimeModelsQueryFilters } from '@/features/admin/working-time-models/overview/helper/workingTimeModelsQueryFilters'
import { useDeleteWorkingTimeModel } from '@/features/admin/working-time-models/overview/mutations/useDeleteWorkingTimeModel'
import {
    useWorkingTimeModels,
    workingTimeModelsQueryOptions,
} from '@/features/admin/working-time-models/overview/queries/useWorkingTimeModels'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import { WorkingTimeModelSortBy } from '@/generated/Enumerations/WorkingTimeModelSortBy_pb'
import { WorkingTimeModelDto } from '@/generated/Messages/Basic_pb'
import { UuidDto } from '@/generated/Messages/Common_pb'
import { debounce } from '@/helper/debounce'
import { durationToDate } from '@/helper/durationToDate'
import { stringifyId } from '@/helper/id'
import { useCreateCUDColumn } from '@/hooks/useCreateCUDColumn'
import {
    searchParamsDeletedItemSelectionDefaultValues,
    searchParamsDeletedItemSelectionSchema,
} from '@/schemas/searchParams/searchParamsDeletedItemSelectionSchema'
import {
    availablePageSizes,
    searchParamsPaginationDefaultValues,
    searchParamsPaginationSchema,
} from '@/schemas/searchParams/searchParamsPaginationSchema'
import {
    searchParamsSearchTextDefaultValues,
    searchParamsSearchTextSchema,
} from '@/schemas/searchParams/searchParamsSearchTextSchema'
import {
    searchParamsSortingDefaultValues,
    searchParamsSortingSchema,
} from '@/schemas/searchParams/searchParamsSortingSchema'
import { OmitDeepMessageFields } from '@/types/OmitDeepMessageFields'

const seleniumPrefix = 'working-time-models'

const timeOnlyFormatter = (value: Date) => `${lightFormat(value, 'HH:mm')} h`

const useStaticColumns = (): GridColDef<WorkingTimeModelDto>[] => {
    const { t } = useTranslation()
    return React.useMemo(
        () => [
            {
                field: 'name',
                headerName: t('common.name'),
                flex: 1,
                minWidth: 150,
                filterable: false,
                hideable: false,
                resizable: false,
            },
            {
                field: 'description',
                headerName: t('common.description'),
                flex: 1,
                minWidth: 200,
                sortable: false,
                filterable: false,
                hideable: false,
                resizable: false,
            },
            {
                field: 'noTrackingBefore',
                headerName: t('workingTimeModel.noTrackingBefore'),
                flex: 1,
                minWidth: 150,
                filterable: false,
                hideable: false,
                resizable: false,
                type: 'dateTime',
                valueFormatter: (value: Date) =>
                    t('common.timeOnly.short', { value }),
                valueGetter: (duration: Duration) =>
                    durationToDate(new Date(), duration),
            },
            {
                field: 'noTrackingAfter',
                headerName: t('workingTimeModel.noTrackingAfter'),
                flex: 1,
                minWidth: 150,
                filterable: false,
                hideable: false,
                resizable: false,
                type: 'dateTime',
                valueFormatter: (value: Date) =>
                    t('common.timeOnly.short', { value }),
                valueGetter: (duration: Duration) =>
                    durationToDate(new Date(), duration),
            },
            {
                field: 'pauseAfter',
                headerName: t('workingTimeModel.pauseAfter'),
                flex: 1,
                minWidth: 150,
                filterable: false,
                hideable: false,
                resizable: false,
                type: 'dateTime',
                valueFormatter: timeOnlyFormatter,
                valueGetter: (duration: Duration) =>
                    durationToDate(new Date(), duration),
            },
            {
                field: 'pauseDuration',
                headerName: t('workingTimeModel.pauseDuration'),
                flex: 1,
                minWidth: 150,
                filterable: false,
                hideable: false,
                resizable: false,
                type: 'dateTime',
                valueFormatter: timeOnlyFormatter,
                valueGetter: (duration: Duration) =>
                    durationToDate(new Date(), duration),
            },
            {
                field: 'maximumWorkTimePerDay',
                headerName: t('workingTimeModel.maximumWorkTimePerDay'),
                flex: 1,
                minWidth: 150,
                filterable: false,
                hideable: false,
                resizable: false,
                type: 'dateTime',
                valueFormatter: timeOnlyFormatter,
                valueGetter: (duration: Duration) =>
                    durationToDate(new Date(), duration),
            },
            {
                field: 'allowOverlappingTrackings',
                headerName: t('workingTimeModel.allowOverlappingTrackings'),
                flex: 1,
                minWidth: 100,
                filterable: false,
                hideable: false,
                resizable: false,
                type: 'boolean',
            },

            {
                field: 'deletedAt',
                headerName: t('common.active'),
                flex: 0.5,
                minWidth: 100,
                valueGetter: (_, row) => !row.timestamps?.deletedAt,
                sortable: false,
                hideable: false,
                resizable: false,

                type: 'boolean',
                filterable: true,
            },
        ],
        [t]
    )
}

const RouteComponent: React.FC = () => {
    const {
        deletedItemSelection,
        page,
        pageSize,
        sortByField,
        sortDirectionDesc,
        searchText,
    } = Route.useSearch()
    const navigate = Route.useNavigate()

    const { data } = useWorkingTimeModels(
        workingTimeModelsQueryFilters(
            deletedItemSelection,
            pageSize,
            page,
            sortByField,
            sortDirectionDesc,
            searchText
        )
    )

    const deleteWorkingTimeModel = useDeleteWorkingTimeModel()
    const [workingTimeModelToDelete, setWorkingTimeModelToDelete] =
        React.useState<UuidDto>()
    const cudColumn = useCreateCUDColumn<WorkingTimeModelDto>({
        viewLinkProps: row => ({
            to: '/admin/working-time-models/$id/view',
            params: { id: stringifyId(row.id) },
        }),
        editLinkProps: row => ({
            to: '/admin/working-time-models/$id/edit',
            params: { id: stringifyId(row.id) },
            disabled: !!row.timestamps?.deletedAt,
        }),
        deleteProps: row => ({
            onClick: () => setWorkingTimeModelToDelete(row.id),
            disabled: !!row.timestamps?.deletedAt,
        }),
    })
    const staticColumns = useStaticColumns()
    const { t } = useTranslation()

    const columns = React.useMemo<GridColDef<WorkingTimeModelDto>[]>(
        () => [...staticColumns, cudColumn],
        [staticColumns, cudColumn]
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onChangeSearchText = React.useCallback(
        debounce(async (searchText: string) => {
            await navigate({
                search: prev => ({ ...prev, searchText, page: 0 }),
            })
        }),
        []
    )

    const paginationModel = React.useMemo(
        (): GridPaginationModel => ({ page, pageSize }),
        [page, pageSize]
    )
    const sortModel = React.useMemo(
        (): GridSortModel =>
            (
                sortByField !== WorkingTimeModelSortBy.UNSPECIFIED &&
                workingTimeModelFieldNameAndSortByEnumMap[sortByField]
            ) ?
                [
                    {
                        field: workingTimeModelFieldNameAndSortByEnumMap[
                            sortByField
                        ],
                        sort: sortDirectionDesc ? 'desc' : 'asc',
                    },
                ]
            :   [],
        [sortByField, sortDirectionDesc]
    )
    const filterModel = React.useMemo(
        () =>
            deletedItemSelection !== DeletedItemSelection.BOTH ?
                {
                    items: [
                        {
                            field: 'deletedAt',
                            operator: 'is',
                            value:
                                deletedItemSelection ===
                                DeletedItemSelection.NORMAL_ONLY,
                        },
                    ],
                }
            :   undefined,
        [deletedItemSelection]
    )

    return (
        <div>
            <title>{t('workingTimeModel.urlIndex')}</title>

            <DataGrid
                rows={data.workingTimeModels}
                columns={columns}
                pageSizeOptions={availablePageSizes}
                disableRowSelectionOnClick={true}
                getRowId={({ id }) => stringifyId(id)}
                slots={{ toolbar: CustomAdminDataGridToolbar }}
                slotProps={{
                    toolbar: {
                        createButton: (
                            <FabLink
                                variant={'extended'}
                                color={'primary'}
                                disabled={false}
                                to={'/admin/working-time-models/create'}
                                sx={{
                                    display: { xs: 'none', sm: 'inline-flex' },
                                }}
                                data-selenium={`${seleniumPrefix}-new`}
                            >
                                <AddOutlined /> {t('common.new')}
                            </FabLink>
                        ),
                        initialSearchText: searchText,
                        onChangeSearchText,
                    },
                }}
                paginationMode={'server'}
                filterMode={'server'}
                sortingMode={'server'}
                rowCount={data.pagination?.totalItemCount ?? 0}
                paginationModel={paginationModel}
                sortModel={sortModel}
                filterModel={filterModel}
                onPaginationModelChange={async model =>
                    await navigate({ search: prev => ({ ...prev, ...model }) })
                }
                onFilterModelChange={async model => {
                    const deletedItemSelection = model.items.find(
                        i => i.field === 'deletedAt'
                    )?.value as boolean | undefined

                    await navigate({
                        search: prev => ({
                            ...prev,

                            page: 0,

                            deletedItemSelection:
                                deletedItemSelection === undefined ?
                                    DeletedItemSelection.BOTH
                                : deletedItemSelection ?
                                    DeletedItemSelection.NORMAL_ONLY
                                :   DeletedItemSelection.DELETED_ONLY,
                        }),
                    })
                }}
                onSortModelChange={async ([model]) => {
                    const castField = model?.field as
                        | keyof OmitDeepMessageFields<WorkingTimeModelDto>
                        | undefined
                    const field =
                        castField ?
                            workingTimeModelFieldNameAndSortByEnumMap[castField]
                        :   undefined

                    await navigate({
                        search: prev => ({
                            ...prev,

                            page: 0,

                            sortByField:
                                field ?? WorkingTimeModelSortBy.UNSPECIFIED,
                            sortDirectionDesc: model?.sort === 'desc',
                        }),
                    })
                }}
                showToolbar={true}
            />

            <FabLink
                variant={'extended'}
                color={'primary'}
                disabled={false}
                to={'/admin/working-time-models/create'}
                sx={theme => ({
                    display: { xs: 'inline-flex', sm: 'none' },
                    position: 'absolute',
                    bottom: theme.spacing(2),
                    right: theme.spacing(2),
                })}
                data-selenium={`${seleniumPrefix}-new`}
            >
                <AddOutlined />
                {t('common.new')}
            </FabLink>
            <ConfirmationDialog
                open={!!workingTimeModelToDelete}
                onClickNo={() => setWorkingTimeModelToDelete(undefined)}
                onClickYes={async () => {
                    if (!workingTimeModelToDelete) {
                        return
                    }

                    setWorkingTimeModelToDelete(undefined)
                    await deleteWorkingTimeModel.mutateAsync(
                        workingTimeModelToDelete
                    )
                }}
            />
        </div>
    )
}

const PendingComponent: React.FC = () => {
    const { page, pageSize, searchText } = Route.useSearch()
    const cudColumn = useCreateCUDColumn<WorkingTimeModelDto>()
    const staticColumns = useStaticColumns()
    const { t } = useTranslation()

    const columns = React.useMemo(
        () => [...staticColumns, cudColumn],
        [cudColumn, staticColumns]
    )

    return (
        <AdminOverviewPagePendingComponent
            pageTitle={t('workingTimeModel.urlIndex')}
            columns={columns}
            page={page}
            pageSize={pageSize}
            searchText={searchText}
        />
    )
}

export const Route = createFileRoute(
    '/__base-layout/admin/working-time-models/'
)({
    component: RouteComponent,
    validateSearch: z.object({
        ...searchParamsDeletedItemSelectionSchema.shape,
        ...searchParamsPaginationSchema.shape,
        ...searchParamsSortingSchema(
            WorkingTimeModelSortBy,
            WorkingTimeModelSortBy.UNSPECIFIED
        ).shape,
        ...searchParamsSearchTextSchema.shape,
    }),
    search: {
        middlewares: [
            stripSearchParams({
                ...searchParamsDeletedItemSelectionDefaultValues,
                ...searchParamsPaginationDefaultValues,
                ...searchParamsSortingDefaultValues(
                    WorkingTimeModelSortBy.UNSPECIFIED
                ),
                ...searchParamsSearchTextDefaultValues,
            }),
        ],
    },

    beforeLoad: ({
        search: {
            deletedItemSelection,
            page,
            pageSize,
            sortByField,
            sortDirectionDesc,
            searchText,
        },
        context: { queryClient, grpcClients, user },
    }) => {
        assertUserIsAdminOrRedirect(user)

        return queryClient.ensureQueryData(
            workingTimeModelsQueryOptions(
                grpcClients,
                workingTimeModelsQueryFilters(
                    deletedItemSelection,
                    pageSize,
                    page,
                    sortByField,
                    sortDirectionDesc,
                    searchText
                )
            )
        )
    },
    pendingMs: 2000,
    pendingComponent: PendingComponent,
})
